import { useTu } from "@src/appV2/i18n/useTu";
import { Box, Stack } from "@mui/material";
import { type UseModalState } from "@src/lib/ui-react";
import { Button } from "../../components/Button";
import { Icon, type IconType } from "../../components/Icon";
import { ResponsiveDialog } from "../../components/ResponsiveDialog";
import { Text } from "../../components/Text";

interface PushNotificationPromptProps {
  modalState: UseModalState;
  onEnable: () => void;
  onDismiss: () => void;
}

export function PushNotificationPrompt(props: PushNotificationPromptProps) {
  const { tu } = useTu("pushNotifications");

  const { modalState, onEnable, onDismiss } = props;
  const notificationBenefits: Array<{ iconType: IconType; title: string; description: string }> = [
    {
      iconType: "briefcase",
      title: tu("Shift bookings"),
      description: tu("Know the moment a shift is claimed or cancelled."),
    },
    {
      iconType: "message",
      title: tu("Chat messages"),
      description: tu("Reply to professionals without keeping a tab open."),
    },
    {
      iconType: "calendar-day",
      title: tu("Schedule changes"),
      description: tu("Stay on top of last-minute updates to your calendar."),
    },
  ];

  const handleEnable = () => {
    onEnable();
    modalState.closeModal();
  };

  const handleDismiss = () => {
    onDismiss();
    modalState.closeModal();
  };

  return (
    <ResponsiveDialog
      footerContent={
        <Box
          sx={(theme) => ({
            paddingX: theme.spacing(5),
            paddingY: theme.spacing(3),
          })}
        >
          <Stack spacing={3}>
            <Button
              fullWidth
              size="large"
              variant="primary"
              startIconType="bell-solid"
              onClick={handleEnable}
            >
              {tu("Enable notifications")}
            </Button>
            <Button fullWidth variant="muted" onClick={handleDismiss}>
              {tu("Maybe later")}
            </Button>
          </Stack>
        </Box>
      }
      modalState={modalState}
      title={tu("Stay up to date")}
      onClose={handleDismiss}
    >
      <Stack spacing={5} sx={{ alignItems: "center" }}>
        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 52,
            height: 52,
            borderRadius: theme.borderRadius?.large,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            boxShadow: theme.shadow?.card,
          })}
        >
          <Icon
            aria-hidden
            type="bell-solid"
            size="large"
            sx={{ width: "1.375rem", height: "1.375rem" }}
          />
        </Box>

        <Text variant="body1" sx={{ textAlign: "center" }}>
          {tu("Turn on push notifications to get timely updates — even when the app is closed.")}
        </Text>

        <Stack spacing={3} sx={{ width: "100%" }}>
          {notificationBenefits.map((benefit) => (
            <Stack
              key={benefit.title}
              direction="row"
              spacing={3}
              sx={(theme) => ({
                alignItems: "flex-start",
                padding: theme.spacing(3),
                borderRadius: theme.borderRadius?.large,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.border?.muted}`,
              })}
            >
              <Box
                sx={(theme) => ({
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  width: "2.25rem",
                  height: "2.25rem",
                  borderRadius: theme.borderRadius?.medium,
                  backgroundColor: theme.palette.background.standout,
                  color: theme.palette.primary.main,
                })}
              >
                <Icon aria-hidden type={benefit.iconType} size="medium" />
              </Box>
              <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                <Text semibold variant="body2">
                  {benefit.title}
                </Text>
                <Text variant="caption" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                  {benefit.description}
                </Text>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </ResponsiveDialog>
  );
}
